// Recent administrative changes.
// Route: /settings/audit-logs

import { useState } from "react";

import { http, qs } from "../api";
import {
  Badge,
  Empty,
  Table,
  useAsync,
  type Column,
} from "../ui";
import { ErrorBanner, fmtDateTime } from "./shared";

export interface AuditLogRow {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  organization_id: string;
  actor_id: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  diff: Record<string, unknown>;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
}

// ============================================================ AuditLogsView

export function AuditLogsView() {
  // Filters are applied on submit, not on keystroke: the endpoint matches an
  // action name exactly, so a partially-typed one would only ever return zero.
  const [actionDraft, setActionDraft] = useState("");
  const [targetDraft, setTargetDraft] = useState("");
  const [action, setAction] = useState("");
  const [targetType, setTargetType] = useState("");

  const { data, error, loading, reload } = useAsync<AuditLogRow[]>(
    () => http.get<AuditLogRow[]>(`/audit-logs${qs({ action, target_type: targetType, limit: 100 })}`),
    [action, targetType]
  );
  const logs = data ?? [];

  const apply = () => {
    setAction(actionDraft.trim());
    setTargetType(targetDraft.trim());
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") apply();
  };

  const columns: Column<AuditLogRow>[] = [
    {
      key: "created_at",
      header: "Time",
      sortable: true,
      render: (l) => <span className="nowrap muted">{fmtDateTime(l.created_at, "—")}</span>,
    },
    { key: "action", header: "Action", sortable: true, render: (l) => <Badge>{l.action}</Badge> },
    {
      key: "target_type",
      header: "Target",
      sortable: true,
      render: (l) => (
        <div>
          <div>{l.target_type}</div>
          {l.target_id && (
            <div className="muted truncate" style={{ maxWidth: 220 }}>
              {l.target_id}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "diff",
      header: "Diff",
      render: (l) => (
        <pre className="pre-block" style={{ maxWidth: 420, marginBottom: 0 }}>
          {JSON.stringify(l.diff ?? {}, null, 2)}
        </pre>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Audit logs</h1>
          <div className="sub">Durable admin and intelligence changes, with redacted diffs.</div>
        </div>
        <div className="page-actions">
          <button className="btn" disabled={loading} onClick={reload}>
            {loading && <span className="spinner" />}
            Refresh
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the audit logs" />

      <div className="toolbar">
        <input
          value={actionDraft}
          placeholder="Action"
          aria-label="Filter by action"
          style={{ width: 220 }}
          onChange={(e) => setActionDraft(e.target.value)}
          onKeyDown={onKey}
        />
        <input
          value={targetDraft}
          placeholder="Target type"
          aria-label="Filter by target type"
          style={{ width: 220 }}
          onChange={(e) => setTargetDraft(e.target.value)}
          onKeyDown={onKey}
        />
        <button className="btn" disabled={loading} onClick={apply}>
          Apply
        </button>
      </div>

      <div className="card flush">
        <div className="card-body">
          <Table
            columns={columns}
            rows={logs}
            loading={loading}
            empty={
              <Empty
                title="No audit logs found"
                body={
                  action || targetType
                    ? "Nothing matches those filters. Action and target type must match exactly."
                    : "Admin changes appear here as they happen."
                }
                icon="▤"
              />
            }
          />
        </div>
        <div className="card-foot">
          {/* Not paginated by design: the endpoint answers the most recent 100. */}
          <span className="muted">
            Showing the {logs.length} most recent entries. This view is capped at 100 and is not
            paginated.
          </span>
        </div>
      </div>
    </div>
  );
}
